<template>
  <div class="h-full overflow-y-auto px-6">
    <!-- Header & Filter -->
    <div class="mb-8 flex justify-between items-end mt-2">
      <div>
        <h2 class="text-lg font-bold text-gray-800">
          Manage your income tax responsibilities: Monthly Challans and Quarterly Returns (Form 24Q)
        </h2>
      </div>
      <div class="flex gap-4 justify-end">
        <div class="w-40">
          <v-select
            v-model="selectedYear"
            :items="years"
            item-text="text"
            item-value="value"
            label="Select Financial Year"
            outlined
            dense
            hide-details
            class="bg-white rounded-lg"
          ></v-select>
        </div>
        <div class="w-64">
          <v-select
            v-model="form24Q.quarter"
            :items="['Q1 (Apr-Jun)', 'Q2 (Jul-Sep)', 'Q3 (Oct-Dec)', 'Q4 (Jan-Mar)']"
            label="Select Quarter"
            outlined
            dense
            hide-details
            class="bg-white rounded-lg"
          ></v-select>
        </div>
      </div>
    </div>

    <!-- Main Content: Form 24Q -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-10">
      <div class="p-8">
        <v-form>
          <!-- 1. Particulars of Statement -->
          <div class="mb-8">
            <h4
              class="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 pb-2 border-b border-gray-100"
            >
              1. Particulars of Deductor (Employer)
            </h4>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <v-text-field
                v-model="form24Q.financialYear"
                label="Financial Year"
                outlined
                dense
                readonly
              ></v-text-field>
              <v-text-field v-model="form24Q.tan" label="TAN" outlined dense></v-text-field>
              <v-text-field v-model="form24Q.pan" label="PAN" outlined dense></v-text-field>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <v-text-field
                v-model="form24Q.verifierName"
                label="Name of Deductor"
                outlined
                dense
              ></v-text-field>
              <v-select
                v-model="form24Q.typeOfDeductor"
                :items="[
                  'Central Govt',
                  'State Govt',
                  'Statutory Body',
                  'Company',
                  'Firm',
                  'Individual/HUF'
                ]"
                label="Type of Deductor"
                outlined
                dense
              ></v-select>
            </div>
            <div class="grid grid-cols-1 gap-6">
              <v-textarea
                v-model="form24Q.address"
                label="Address"
                rows="2"
                outlined
                dense
                hide-details
              ></v-textarea>
            </div>
          </div>

          <!-- 2. Particulars of Person Responsible -->
          <div class="mb-8">
            <h4
              class="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 pb-2 border-b border-gray-100"
            >
              2. Particulars of Person Responsible for Deduction of Tax
            </h4>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
              <v-text-field
                v-model="form24Q.responsiblePersonName"
                label="Name"
                outlined
                dense
              ></v-text-field>
              <v-text-field
                v-model="form24Q.responsiblePersonDesignation"
                label="Designation"
                outlined
                dense
              ></v-text-field>
              <v-text-field
                v-model="form24Q.responsiblePersonPan"
                label="PAN"
                outlined
                dense
              ></v-text-field>
            </div>
          </div>

          <!-- 3. Details of Tax Deducted and Paid (Challan Details) -->
          <div class="mb-8">
            <div class="flex justify-between items-center mb-4 pb-2 border-b border-gray-100">
              <div>
                <h4 class="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                  3. Challan Details (Annexure I)
                </h4>
                <p class="text-xs text-gray-500 mt-1">
                  Amount paid against challans in this quarter
                </p>
              </div>
              <v-btn small color="primary" depressed @click="openAddChallanDialog">
                <v-icon left small>mdi-plus</v-icon>
                Add Challan
              </v-btn>
            </div>

            <v-simple-table dense class="border rounded-lg">
              <template v-slot:default>
                <thead>
                  <tr class="bg-gray-50">
                    <th>Sr. No.</th>
                    <th>Details of Challan</th>
                    <th>Date of Payment</th>
                    <th>Challan Serial No.</th>
                    <th>Total Amount (₹)</th>
                    <th class="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(challan, index) in form24Q.challans" :key="index">
                    <td class="px-4">{{ index + 1 }}</td>
                    <td class="px-4">TDS Payment</td>
                    <td class="px-4">{{ challan.date }}</td>
                    <td class="px-4">{{ challan.serialNo }}</td>
                    <td class="px-4">₹ {{ formatNumber(challan.amount) }}</td>
                    <td class="text-center">
                      <v-btn
                        icon
                        small
                        color="primary"
                        class="mr-2"
                        @click="openEditChallan(challan)"
                      >
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn icon small color="error" @click="confirmDeleteChallan(challan)">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                  <tr v-if="form24Q.challans.length === 0">
                    <td colspan="6" class="text-center py-4 text-gray-500">
                      No challans found for this period. Click 'Add Challan' to record a payment.
                    </td>
                  </tr>
                  <tr class="bg-blue-50/30">
                    <td colspan="4" class="text-right font-bold text-gray-700">
                      Total TDS Deposited:
                    </td>
                    <td class="px-4 font-bold text-blue-800">
                      ₹ {{ formatNumber(form24Q.totalTdsDeposited) }}
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>

          <!-- Sign/Submit -->
          <div class="flex justify-end pt-4 border-t border-gray-100 gap-4">
            <v-btn
              color="primary"
              large
              depressed
              class="px-8 text-capitalize font-bold"
              @click="generate24Q"
            >
              Generate & Download Form24Q
            </v-btn>
          </div>
        </v-form>
      </div>
    </div>

    <!-- Dialog for Monthly Challan Form -->
    <v-dialog v-model="challanDialog" max-width="900px" persistent>
      <v-card class="bg-gray-50 px-4">
        <v-toolbar flat color="white" class="border-b border-gray-100">
          <v-toolbar-title class="font-bold text-gray-800 text-lg">
            {{ isEditing ? 'Edit Monthly Challan' : 'Add Monthly Challan' }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeChallanDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-6 relative">
          <!-- Month Selection inside Dialog -->
          <!-- Month Selection inside Dialog -->
          <!-- Month Selection inside Dialog -->
          <!-- Month Selection inside Dialog -->
          <div class="flex flex-col gap-4 mb-6">
            <div class="flex gap-4">
              <v-select
                v-model="challanFormYear"
                :items="years"
                item-text="text"
                item-value="value"
                label="Select Financial Year"
                outlined
                dense
                hide-details
                class="bg-white rounded-lg w-1/2"
                :disabled="isEditing"
              ></v-select>

              <v-select
                v-model="dialogMonth"
                :items="challanMonthOptions"
                item-text="text"
                item-value="value"
                label="Select Month for Challan"
                outlined
                dense
                hide-details
                class="bg-white rounded-lg w-1/2"
                :disabled="isEditing"
                :no-data-text="'Select Year first'"
              ></v-select>
            </div>

            <div v-if="isEditing">
              <v-chip color="blue lighten-5" text-color="blue darken-3" small class="font-bold">
                Editing Record: {{ formData.challanNo }}
              </v-chip>
            </div>
          </div>

          <!-- Stats Section (Context regarding the selected month) -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" v-if="dialogMonth">
            <!-- Pending Tax Card -->
            <div
              class="bg-gradient-to-br from-[#1E293B] to-[#334155] rounded-xl p-6 text-white shadow-lg relative overflow-hidden group"
            >
              <div
                class="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity"
              >
                <v-icon size="64" color="white">mdi-cash-remove</v-icon>
              </div>
              <div>
                <p class="text-gray-300 text-sm font-medium mb-1">
                  {{ paidTaxForMonth > 0 ? 'Remaining Pending Tax' : 'Total Pending Tax' }}
                </p>
                <h2 class="text-3xl font-bold">
                  ₹
                  {{ formatNumber(paidTaxForMonth > 0 ? remainingPendingTax : totalPendingTax) }}
                </h2>
                <p class="text-xs text-gray-400 mt-2 flex items-center">
                  <span class="inline-block w-2 h-2 rounded-full bg-yellow-400 mr-2"></span>
                  Based on {{ pendingLogsCount }} logs for {{ dialogMonth }}
                </p>
                <p v-if="paidTaxForMonth > 0" class="text-xs text-yellow-200 mt-1">
                  (Total: ₹ {{ formatNumber(totalPendingTax) }} | Paid: ₹
                  {{ formatNumber(paidTaxForMonth) }})
                </p>
              </div>
            </div>

            <!-- Paid Tax Card (Contextual info if editing or viewing existing) -->
            <div
              v-if="currentMonthChallans.length > 0"
              class="bg-gradient-to-br from-green-600 to-green-800 rounded-xl p-6 text-white shadow-lg relative overflow-hidden group"
            >
              <div
                class="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity"
              >
                <v-icon size="64" color="white">mdi-check-circle-outline</v-icon>
              </div>
              <div>
                <p class="text-gray-100 text-sm font-medium mb-1">Total Paid ({{ dialogMonth }})</p>
                <h2 class="text-3xl font-bold">₹ {{ formatNumber(paidTaxForMonth) }}</h2>
                <p class="text-xs text-gray-200 mt-2 flex items-center">
                  <span class="inline-block w-2 h-2 rounded-full bg-white mr-2"></span>
                  {{ currentMonthChallans.length }} Challan(s) Filed
                </p>
              </div>
            </div>
          </div>

          <!-- The Challan Form -->
          <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <v-form ref="challanForm" v-model="formValid">
              <!-- Section 1: Tax Applicable -->
              <div class="mb-6">
                <h4
                  class="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-2 border-b border-gray-100 pb-1"
                >
                  Tax Applicable
                </h4>
                <v-radio-group v-model="formData.taxApplicable" row hide-details class="mt-0 pt-0">
                  <v-radio label="(0020) Companies" value="0020"></v-radio>
                  <v-radio label="(0021) Other than Companies" value="0021"></v-radio>
                </v-radio-group>
              </div>

              <!-- Section 2: Type of Payment -->
              <div class="mb-6">
                <h4
                  class="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-2 border-b border-gray-100 pb-1"
                >
                  Type of Payment
                </h4>
                <v-radio-group v-model="formData.paymentType" row hide-details class="mt-0 pt-0">
                  <v-radio label="(200) TDS Payable" value="200"></v-radio>
                  <v-radio label="(400) TDS Regular Assessment" value="400"></v-radio>
                </v-radio-group>
              </div>

              <!-- Section 3: Payment Details -->
              <div class="mb-6">
                <h4
                  class="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 border-b border-gray-100 pb-1"
                >
                  Details of Payment
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <v-text-field
                    v-model="formData.incomeTax"
                    label="Income Tax"
                    prefix="₹"
                    type="number"
                    outlined
                    dense
                    hide-details="auto"
                    :rules="[
                      (v) => !!v || 'Income Tax is required',
                      (v) =>
                        parseFloat(v) <= remainingPendingTax + (isEditing ? editingIncomeTax : 0) ||
                        `Cannot exceed remaining pending tax (₹ ${formatNumber(
                          remainingPendingTax + (isEditing ? editingIncomeTax : 0)
                        )})`
                    ]"
                  ></v-text-field>
                  <v-text-field
                    v-model="formData.surcharge"
                    label="Surcharge"
                    prefix="₹"
                    type="number"
                    outlined
                    dense
                    hide-details="auto"
                  ></v-text-field>
                  <v-text-field
                    v-model="formData.educationCess"
                    label="Education Cess"
                    prefix="₹"
                    type="number"
                    outlined
                    dense
                    hide-details="auto"
                  ></v-text-field>
                  <v-text-field
                    v-model="formData.interest"
                    label="Interest"
                    prefix="₹"
                    type="number"
                    outlined
                    dense
                    hide-details="auto"
                  ></v-text-field>
                  <v-text-field
                    v-model="formData.penalty"
                    label="Penalty"
                    prefix="₹"
                    type="number"
                    outlined
                    dense
                    hide-details="auto"
                  ></v-text-field>
                  <v-text-field
                    v-model="formData.others"
                    label="Others"
                    prefix="₹"
                    type="number"
                    outlined
                    dense
                    hide-details="auto"
                  ></v-text-field>
                </div>
                <div
                  class="mt-4 p-3 bg-blue-50/50 rounded-lg flex justify-between items-center text-blue-900 border border-blue-100"
                >
                  <span class="font-semibold">Total Amount</span>
                  <span class="font-bold text-xl">₹ {{ formatNumber(totalPaymentAmount) }}</span>
                </div>
              </div>

              <!-- Section 4: General Info -->
              <div class="mb-6">
                <h4
                  class="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 border-b border-gray-100 pb-1"
                >
                  Assessment & Bank Details
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <!-- <v-select
                    v-model="formData.assessmentYear"
                    :items="assessmentYears"
                    label="Assessment Year"
                    outlined
                    dense
                    hide-details="auto"
                  ></v-select> -->

                  <v-select
                    v-model="selectedBranch"
                    :items="branches"
                    item-text="branchName"
                    item-value="_id"
                    outlined
                    dense
                    hide-details="auto"
                    label="Branch Location"
                    @change="fetchBankDetails()"
                    :rules="[(v) => !!v || 'Branch selection is required']"
                  ></v-select>
                  <v-select
                    v-model="formData.bankDetailsId"
                    :items="banks"
                    item-text="bankName"
                    item-value="_id"
                    label="Bank Name *"
                    outlined
                    dense
                    :disabled="!selectedBranch"
                    hide-details="auto"
                    :rules="[(v) => !!v || 'Bank selection is required']"
                  ></v-select>
                  <v-text-field
                    v-model="formData.challanNo"
                    label="Challan Serial No."
                    outlined
                    dense
                    hide-details
                  ></v-text-field>
                  <v-menu
                    v-model="dateMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="formData.date"
                        label="Date of Payment"
                        append-icon="mdi-calendar"
                        readonly
                        outlined
                        dense
                        hide-details
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="formData.date"
                      @input="dateMenu = false"
                    ></v-date-picker>
                  </v-menu>
                  <!-- <v-text-field
                    v-model="formData.tan"
                    label="Tax Deduction Account No. (TAN)"
                    outlined
                    dense
                    hide-details="auto"
                    class="md:col-span-2"
                  ></v-text-field> -->
                </div>
              </div>
            </v-form>
          </div>
        </v-card-text>

        <v-card-actions class="p-6 bg-white border-t border-gray-100">
          <v-spacer></v-spacer>
          <v-btn text class="normal-case px-6" @click="closeChallanDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            depressed
            class="px-8 font-bold text-capitalize"
            :loading="submitting"
            @click="submitChallan"
          >
            {{ isEditing ? 'Update Challan' : 'Save Challan' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog via Global Component -->
    <AlertPopUp
      :showConfirmationDialog="deleteChallanDialog"
      confirmationTitle="Delete Challan?"
      confirmationMessage="Are you sure you want to delete this challan record? This action cannot be undone."
      confirmationActionText="Delete"
      confirmationActionColor="error"
      cancelActionText="Cancel"
      :loading="deleting"
      :cancel="cancelDelete"
      :performAction="deleteChallan"
    />
  </div>
</template>

<script>
import { generateForm24QHtml } from '@/utils/workforce/form24QGenerator'
import AlertPopUp from '@/components/AlertPopUp.vue'

export default {
  name: 'TaxSubmission',
  components: {
    AlertPopUp
  },
  props: {
    branches: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      allLogs: [],
      loading: false,
      formValid: true,
      submitting: false,
      dateMenu: false,

      // Dialog Control
      challanDialog: false,
      deleteChallanDialog: false,
      challanToDeleteId: null,
      deleting: false,
      isEditing: false,
      editingChallanId: null,
      editingIncomeTax: 0,
      dialogMonth: new Date().toLocaleString('default', { month: 'long' }),

      challanFormYear: null,

      banks: [],
      selectedYear: null, // Initialized in created/mounted based on FY
      monthNames: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
      ],
      formData: {
        taxApplicable: '0021',
        paymentType: '200',
        incomeTax: 0,
        surcharge: 0,
        educationCess: 0,
        interest: 0,
        penalty: 0,
        others: 0,
        assessmentYear: '',
        tan: '',
        bankDetailsId: '',
        challanNo: '',
        date: new Date().toISOString().substr(0, 10)
      },
      // Form 24Q Data
      form24Q: {
        quarter: 'Q4 (Jan-Mar)',
        financialYear: '2025-26',
        tan: 'BLRP09823C',
        pan: 'AAACH7654K',
        verifierName: 'FinFi Technologies Pvt Ltd',
        typeOfDeductor: 'Company',
        address: 'B-104, Plot No. 844, 1st Floor, Indira Nagar, Bengaluru, Karnataka - 560038',
        responsiblePersonName: 'Gaurav Shukla',
        responsiblePersonDesignation: 'Director',
        responsiblePersonPan: 'BPLPG7845H',
        totalTdsDeposited: 0,
        challans: [],
        deductees: []
      },
      allChallansForFY: [],
      selectedBranch: null
    }
  },
  computed: {
    totalPendingTax() {
      return this.allLogs.reduce((sum, log) => {
        let tax = 0
        if (log.incomeTax && typeof log.incomeTax === 'object') {
          tax = parseFloat(log.incomeTax.amount) || 0
        } else {
          tax = parseFloat(log.incomeTax) || 0
        }
        return sum + tax
      }, 0)
    },
    paidTaxForMonth() {
      return this.allLogs.reduce((sum, log) => {
        const taxObj = log.incomeTax
        if (taxObj && typeof taxObj === 'object' && taxObj.status === 'Settled') {
          return sum + (parseFloat(taxObj.amount) || 0)
        }
        return sum
      }, 0)
    },
    remainingPendingTax() {
      const pending = this.totalPendingTax - this.paidTaxForMonth
      return pending > 0 ? pending : 0
    },
    pendingLogsCount() {
      return this.allLogs.length
    },
    totalPaymentAmount() {
      const { incomeTax, surcharge, educationCess, interest, penalty, others } = this.formData
      return (
        (parseFloat(incomeTax) || 0) +
        (parseFloat(surcharge) || 0) +
        (parseFloat(educationCess) || 0) +
        (parseFloat(interest) || 0) +
        (parseFloat(penalty) || 0) +
        (parseFloat(others) || 0)
      )
    },
    assessmentYears() {
      const currentYear = new Date().getFullYear()
      return [
        `${currentYear + 1}-${currentYear + 2}`,
        `${currentYear}-${currentYear + 1}`,
        `${currentYear - 1}-${currentYear}`
      ]
    },
    years() {
      const today = new Date()
      const currentMonth = today.getMonth() // 0-11
      const currentYear = today.getFullYear()
      // If Jan, Feb, Mar -> previous year is FY Start
      const fyStart = currentMonth < 3 ? currentYear - 1 : currentYear

      return [
        { text: `${fyStart} - ${fyStart + 1}`, value: fyStart }, // Current
        { text: `${fyStart - 1} - ${fyStart}`, value: fyStart - 1 } // Last
      ]
    },
    calculatedFinancialYear() {
      // Based on selectedYear
      // If user selected "2023 - 2024" (value=2023), FY string should be "2023-24" or "2023-2024"
      // The backend/form likely expects "YYYY-YY" or full "YYYY-YYYY"
      // Based on previous code: financialYear: '2025-26', it seems to be YYYY-YY
      if (!this.selectedYear) return ''
      return `${this.selectedYear}-${String(this.selectedYear + 1).slice(-2)}`
    },
    currentMonthChallans() {
      return this.allChallansForFY.filter((c) => c.month === this.dialogMonth)
    },
    challanMonthOptions() {
      if (!this.challanFormYear) return []

      const startYear = this.challanFormYear
      const endYear = this.challanFormYear + 1

      // Full Financial Year List (Apr-Mar)
      return [
        { text: `April ${startYear}`, value: 'April' },
        { text: `May ${startYear}`, value: 'May' },
        { text: `June ${startYear}`, value: 'June' },
        { text: `July ${startYear}`, value: 'July' },
        { text: `August ${startYear}`, value: 'August' },
        { text: `September ${startYear}`, value: 'September' },
        { text: `October ${startYear}`, value: 'October' },
        { text: `November ${startYear}`, value: 'November' },
        { text: `December ${startYear}`, value: 'December' },
        { text: `January ${endYear}`, value: 'January' },
        { text: `February ${endYear}`, value: 'February' },
        { text: `March ${endYear}`, value: 'March' }
      ]
    },
    financialYearMonthOptions() {
      // KEEPING FOR BACKWARD COMPATIBILITY OR IF NEEDED ELSEWHERE
      // But mainly replaced by quarterMonthOptions for the dialog
      if (!this.selectedYear) return []

      const startYear = this.selectedYear
      const endYear = this.selectedYear + 1

      // Apr-Dec of Start Year
      const firstPart = [
        { text: `April ${startYear}`, value: 'April' },
        { text: `May ${startYear}`, value: 'May' },
        { text: `June ${startYear}`, value: 'June' },
        { text: `July ${startYear}`, value: 'July' },
        { text: `August ${startYear}`, value: 'August' },
        { text: `September ${startYear}`, value: 'September' },
        { text: `October ${startYear}`, value: 'October' },
        { text: `November ${startYear}`, value: 'November' },
        { text: `December ${startYear}`, value: 'December' }
      ]

      // Jan-Mar of End Year
      const secondPart = [
        { text: `January ${endYear}`, value: 'January' },
        { text: `February ${endYear}`, value: 'February' },
        { text: `March ${endYear}`, value: 'March' }
      ]

      return [...firstPart, ...secondPart]
    }
  },
  watch: {
    dialogMonth() {
      // Whenever dialog month changes, fetch pending logs to show correct Pending Tax context
      if (this.challanDialog) {
        if (!this.isEditing) {
          this.formData.incomeTax = 0
        }
        this.fetchSalaryLogs()
      }
    },
    challanFormYear() {
      if (this.challanDialog) {
        if (!this.isEditing) {
          this.formData.incomeTax = 0
        }
        this.fetchSalaryLogs()
      }
    },
    'form24Q.quarter'() {
      this.fetchChallanDetails()
    },
    selectedYear(newVal) {
      if (newVal) {
        this.form24Q.financialYear = `${newVal}-${String(newVal + 1).slice(-2)}`
      }
      this.fetchChallanDetails()
    }
  },
  mounted() {
    this.fetchBankDetails()
    // Initialize Financial Year selection
    const today = new Date()
    const currentMonth = today.getMonth() // 0-11
    const currentYear = today.getFullYear()

    // If Jan-Mar, current FY started last year
    if (currentMonth < 3) {
      this.selectedYear = currentYear - 1
    } else {
      this.selectedYear = currentYear
    }

    // Ensure the form24Q financial year is set on mount
    if (this.selectedYear) {
      this.form24Q.financialYear = `${this.selectedYear}-${String(this.selectedYear + 1).slice(-2)}`
    }

    this.fetchChallanDetails()

    // Set default Assessment Year
    this.formData.assessmentYear = this.assessmentYears[1]

    // Watch remainingPendingTax to auto-fill income tax in form (convenience)
    this.$watch('remainingPendingTax', (newVal) => {
      // Only auto-fill if we are adding (not editing) and field is 0
      if (!this.isEditing && this.formData.incomeTax === 0 && newVal > 0) {
        this.formData.incomeTax = newVal
      }
    })
  },
  methods: {
    async fetchBankDetails() {
      try {
        const response = await this.$axios.get('/orgSetup/org-tds-details/')
        if (response.data && response.data.data) {
          const orgTdsData = Array.isArray(response.data.data)
            ? response.data.data[0]
            : response.data.data

          const bankList = orgTdsData?.banks || orgTdsData?.chalanBankDetails || []
          if (bankList.length > 0) {
            this.banks = bankList.filter(
              (bank) => !bank.branchId || bank.branchId === this.selectedBranch
            )
          }
          if (orgTdsData.orgDetails && orgTdsData.orgDetails.tanNumber) {
            this.formData.tan = orgTdsData.orgDetails.tanNumber
          }
        }
      } catch (error) {
        console.error('Error fetching bank details:', error)
      }
    },
    async fetchSalaryLogs() {
      this.loading = true
      try {
        const params = {}
        if (this.dialogMonth && this.challanFormYear) {
          params.salaryMonth = this.dialogMonth

          // Calculate calendar year based on Financial Year and Month
          // FY 2024-25: Apr-Dec is 2024, Jan-Mar is 2025
          const monthIndex = this.monthNames.indexOf(this.dialogMonth)
          // Jan=0, Feb=1, Mar=2  => Next Year
          if (monthIndex >= 0 && monthIndex <= 2) {
            params.salaryYear = this.challanFormYear + 1
          } else {
            params.salaryYear = this.challanFormYear
          }
        }

        const response = await this.$axios.get('/orgSetup/salary-logs', { params })
        this.allLogs = response.data.salaryLogs || []
      } catch (error) {
        console.error('Error fetching salary logs:', error)
        this.$toast.error('Failed to fetch salary logs')
      } finally {
        this.loading = false
      }
    },
    formatNumber(num) {
      return new Intl.NumberFormat('en-IN').format(num)
    },
    async fetchChallanDetails() {
      try {
        const quarterMap = {
          'Q1 (Apr-Jun)': ['April', 'May', 'June'],
          'Q2 (Jul-Sep)': ['July', 'August', 'September'],
          'Q3 (Oct-Dec)': ['October', 'November', 'December'],
          'Q4 (Jan-Mar)': ['January', 'February', 'March']
        }
        const targetMonths = quarterMap[this.form24Q.quarter] || []
        const nextYearMonths = ['January', 'February', 'March']

        // Fetch challans for each month in the quarter with correct calendar year
        const requests = targetMonths.map((month) => {
          const isNextYear = nextYearMonths.includes(month)
          const yearParam = isNextYear ? this.selectedYear + 1 : this.selectedYear

          return this.$axios.get('/orgSetup/org-tds-chalan-details', {
            params: {
              year: yearParam,
              month: month
            }
          })
        })

        const responses = await Promise.all(requests)

        // Combine results from all months
        const allData = responses.flatMap((response) => {
          if (response.data && response.data.data) {
            return Array.isArray(response.data.data) ? response.data.data : [response.data.data]
          }
          return []
        })

        // Deduplicate by _id
        const uniqueChallans = Array.from(new Map(allData.map((item) => [item._id, item])).values())
        this.allChallansForFY = uniqueChallans

        this.form24Q.challans = this.allChallansForFY.map((item) => {
          let totalAmount = item.challanAmount
          // Calculate total if missing or zero, using new schema details
          if (!totalAmount && item.challanPaymentDetails) {
            const d = item.challanPaymentDetails
            totalAmount =
              (d.tdsAmount || 0) +
              (d.educationCess || 0) +
              (d.surcharge || 0) +
              (d.interest || 0) +
              (d.lateFee || 0) +
              (d.others || 0)
          } else if (!totalAmount && item.detailsOfPayment) {
            // Fallback for old schema if any locally
            const d = item.detailsOfPayment
            totalAmount =
              (parseFloat(d.incomeTax) || 0) +
              (parseFloat(d.surcharge) || 0) +
              (parseFloat(d.educationCess) || 0) +
              (parseFloat(d.interest) || 0) +
              (parseFloat(d.penalty) || 0) +
              (parseFloat(d.others) || 0)
          }

          // Ensure the main list object has this calculated amount for "Saved Challan Amount" display
          item.challanAmount = totalAmount || 0

          const details = item.challanPaymentDetails || item.detailsOfPayment || {}
          return {
            date: item.filingDate ? item.filingDate.substring(0, 10) : '',
            serialNo: item.challanId || '',
            // Ensure we fallback to 0 properly
            tds: parseFloat(details.tdsAmount || details.incomeTax) || 0,
            surcharge: parseFloat(details.surcharge) || 0,
            educationCess: parseFloat(details.educationCess) || 0,
            interest: parseFloat(details.interest) || 0,
            others:
              (parseFloat(details.lateFee || details.penalty) || 0) +
              (parseFloat(details.others) || 0),
            amount: totalAmount || 0,
            originalItem: item
          }
        })

        // Calculate total for the quarter
        this.form24Q.totalTdsDeposited = this.form24Q.challans.reduce(
          (sum, c) => sum + (Number(c.amount) || 0),
          0
        )
      } catch (error) {
        console.error('Error fetching challan details:', error)
      }
    },

    // DIALOG METHODS

    openAddChallanDialog() {
      this.isEditing = false
      this.editingChallanId = null
      this.editingIncomeTax = 0
      this.resetFormData()

      // Initialize Year to Current FY Start
      const today = new Date()
      const currentMonth = today.getMonth() // 0-11
      const currentYear = today.getFullYear()
      const fyStart = currentMonth < 3 ? currentYear - 1 : currentYear

      this.challanFormYear = fyStart

      // Initialize Month to current month (if available in options)
      this.$nextTick(() => {
        const currentMonthName = today.toLocaleString('default', { month: 'long' })
        const exists = this.challanMonthOptions.some((m) => m.value === currentMonthName)

        if (exists) {
          this.dialogMonth = currentMonthName
        } else {
          this.dialogMonth = this.challanMonthOptions[0]?.value || 'April'
        }
        this.challanDialog = true
        this.fetchSalaryLogs()
      })
    },
    openEditChallan(challanRow) {
      this.isEditing = true
      const fullData =
        challanRow.originalItem ||
        this.allChallansForFY.find((c) => c.challanId === challanRow.serialNo)

      if (fullData) {
        this.editingChallanId = fullData._id

        // Set Year (using global selected year as context)
        this.challanFormYear = this.selectedYear

        this.$nextTick(() => {
          this.dialogMonth = fullData.month
        })

        // Check for new schema structure first, fallback to old
        const paymentDetails = fullData.challanPaymentDetails || fullData.detailsOfPayment || {}
        this.editingIncomeTax = paymentDetails.tdsAmount || paymentDetails.incomeTax || 0

        this.formData = {
          date: fullData.filingDate
            ? fullData.filingDate.substring(0, 10)
            : new Date().toISOString().substr(0, 10),
          challanNo: fullData.challanId,
          incomeTax: paymentDetails.tdsAmount || paymentDetails.incomeTax || 0,
          surcharge: paymentDetails.surcharge || 0,
          educationCess: paymentDetails.educationCess || 0,
          interest: paymentDetails.interest || 0,
          penalty: paymentDetails.lateFee || paymentDetails.penalty || 0,
          others: paymentDetails.others || 0,

          taxApplicable: fullData.taxApplicable || '0021',
          paymentType: fullData.paymentType || '200',
          assessmentYear: fullData.assessmentYear || this.assessmentYears[1],
          tan: fullData.tan || this.formData.tan, // keep existing if not found
          bankDetailsId: fullData.bankDetailsId || ''
        }
      }

      this.challanDialog = true
      this.fetchSalaryLogs() // Fetch context for that month
    },
    closeChallanDialog() {
      this.challanDialog = false
      this.resetFormData()
    },
    resetFormData() {
      this.formData = {
        taxApplicable: '0021',
        paymentType: '200',
        incomeTax: 0,
        surcharge: 0,
        educationCess: 0,
        interest: 0,
        penalty: 0,
        others: 0,
        assessmentYear: this.assessmentYears[1],
        tan: this.formData.tan, // Keep TAN
        bankDetailsId: '',
        challanNo: '',
        date: new Date().toISOString().substr(0, 10)
      }
    },

    async submitChallan() {
      if (!this.$refs.challanForm.validate()) return

      this.submitting = true
      try {
        const payload = {
          bankDetailsId: this.formData.bankDetailsId,
          year: this.selectedYear,
          month: this.dialogMonth, // Use the dialog's selected month
          challanId: this.formData.challanNo,
          challanAmount: this.totalPaymentAmount,
          filingDate: this.formData.date,
          // Including these for reference/standard usage even if not in user snippet explicit req
          taxApplicable: this.formData.taxApplicable,
          paymentType: this.formData.paymentType,

          challanPaymentDetails: {
            tdsAmount: parseFloat(this.formData.incomeTax) || 0,
            educationCess: parseFloat(this.formData.educationCess) || 0,
            surcharge: parseFloat(this.formData.surcharge) || 0,
            interest: parseFloat(this.formData.interest) || 0,
            lateFee: parseFloat(this.formData.penalty) || 0, // Mapped penalty -> lateFee
            others: parseFloat(this.formData.others) || 0
          },
          assessmentYear: this.formData.assessmentYear
        }

        if (this.isEditing && this.editingChallanId) {
          await this.$axios.put(
            `/orgSetup/org-tds-chalan-details/${this.editingChallanId}`,
            payload
          )
        } else {
          await this.$axios.post('/orgSetup/org-tds-chalan-details', payload)
        }

        this.$toast.success(
          this.isEditing ? 'Challan updated successfully!' : 'Challan record saved successfully!'
        )
        this.closeChallanDialog()
        this.fetchChallanDetails() // Refresh the list
      } catch (error) {
        console.error(error)
        this.$toast.error('Failed to save Challan record')
      } finally {
        this.submitting = false
      }
    },
    async generate24Q() {
      // 1. Identify months for the selected quarter
      const quarterMap = {
        'Q1 (Apr-Jun)': ['April', 'May', 'June'],
        'Q2 (Jul-Sep)': ['July', 'August', 'September'],
        'Q3 (Oct-Dec)': ['October', 'November', 'December'],
        'Q4 (Jan-Mar)': ['January', 'February', 'March']
      }
      const monthsToFetch = quarterMap[this.form24Q.quarter] || []

      // 2. Fetch logs for ALL months in the quarter
      let quarterlyLogs = []
      this.loading = true
      try {
        for (const month of monthsToFetch) {
          const params = { salaryMonth: month }
          const response = await this.$axios.get('/orgSetup/salary-logs', { params })
          if (response.data && response.data.salaryLogs) {
            // Add month info to each log for reference if needed
            const logsWithMonth = response.data.salaryLogs.map((l) => ({
              ...l,
              salaryMonth: month
            }))
            quarterlyLogs = [...quarterlyLogs, ...logsWithMonth]
          }
        }
      } catch (error) {
        console.error('Error fetching quarterly logs:', error)
        this.$toast.error('Failed to fetch data for all months')
        this.loading = false
        return
      } finally {
        this.loading = false
      }

      // 3. Map to Deductees
      if (quarterlyLogs.length > 0) {
        this.form24Q.deductees = quarterlyLogs.map((log) => {
          const user = log.employee || {}
          const salary = parseFloat(user.ctc) / 12 || 0

          const tax =
            typeof log.incomeTax === 'object'
              ? parseFloat(log.incomeTax.amount) || 0
              : parseFloat(log.incomeTax) || 0

          return {
            employeeId: user.employeeId || '',
            name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
            pan: (() => {
              let p = user.panCardNumber || ''
              if (
                user.employmentDetails &&
                user.employmentDetails.identityDetails &&
                Array.isArray(user.employmentDetails.identityDetails)
              ) {
                const panObj = user.employmentDetails.identityDetails.find(
                  (id) => id.idType === 'PAN'
                )
                if (panObj && panObj.idNumber) {
                  p = panObj.idNumber
                }
              }
              return p
            })(),
            paymentDate: log.incomeTax.settlementDate
              ? this.$dayjs(log.incomeTax.settlementDate).format('DD-MM-YYYY HH:mm A')
              : '',
            totalSalary: salary.toFixed(2),
            tdsDeducted: tax.toFixed(2)
          }
        })
      } else {
        this.form24Q.deductees = []
      }
      console.log(this.form24Q)
      // return
      const htmlContent = generateForm24QHtml(this.form24Q)
      const { financialYear, quarter } = this.form24Q

      const blob = new Blob([htmlContent], { type: 'text/html' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `Form_24Q_${quarter.split(' ')[0]}_${financialYear}.html`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      this.$toast.success('Form 24Q HTML Downloaded Successfully!')
    },
    confirmDeleteChallan(challan) {
      if (challan.originalItem && challan.originalItem._id) {
        this.challanToDeleteId = challan.originalItem._id
        this.deleteChallanDialog = true
      }
    },
    cancelDelete() {
      this.deleteChallanDialog = false
      this.challanToDeleteId = null
    },
    async deleteChallan() {
      console.log(this.challanToDeleteId)
      if (!this.challanToDeleteId) return

      this.deleting = true
      try {
        await this.$axios.delete(`/orgSetup/org-tds-chalan-details/${this.challanToDeleteId}`)
        this.$toast.success('Challan deleted successfully')
        this.deleteChallanDialog = false
        this.fetchChallanDetails() // Refresh list
      } catch (error) {
        console.error('Failed to delete challan:', error)
        this.$toast.error('Failed to delete challan')
      } finally {
        this.deleting = false
        this.challanToDeleteId = null
      }
    }
  }
}
</script>

<style scoped>
/* Add any specific styles if needed, mostly using Tailwind */
</style>
